function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatCell(value, type) {
  if (value == null || value === '') return '—';
  if (type === 'date') {
    try {
      return new Date(value).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch (_err) {
      return String(value);
    }
  }
  return String(value);
}

function renderTable(columns, rows) {
  const head = columns
    .map((col) => `<th>${escapeHtml(col.label || col.key)}</th>`)
    .join('');

  const body = rows
    .map((row) => {
      const cells = columns
        .map((col) => `<td>${escapeHtml(formatCell(row[col.key], col.type))}</td>`)
        .join('');
      return `<tr>${cells}</tr>`;
    })
    .join('');

  return `
    <table>
      <thead><tr>${head}</tr></thead>
      <tbody>${body || `<tr><td colspan="${columns.length}">No rows</td></tr>`}</tbody>
    </table>
  `;
}

/**
 * Opens a print window so the user can Save as PDF from the browser print dialog.
 */
export function openReportPrintWindow(report) {
  if (!report) throw new Error('Generate a report first');

  const columns = Array.isArray(report.columns) ? report.columns : [];
  const rows = Array.isArray(report.rows) ? report.rows : [];
  const summary = report.summary || {};
  const meta = report.meta || {};
  const vehicle = meta.vehicle || {};
  const range = meta.range || {};
  const vehicleSummary = report.vehicleSummary || null;

  const title = meta.title || report.title || 'Fleet Report';
  const subtitleParts = [
    vehicle.name ? `${vehicle.name}${vehicle.plate ? ` (${vehicle.plate})` : ''}` : null,
    range.from && range.to
      ? `${String(range.from).slice(0, 10)} → ${String(range.to).slice(0, 10)}`
      : null,
    `${rows.length} row${rows.length === 1 ? '' : 's'}`,
  ].filter(Boolean);

  const cards = Array.isArray(summary.cards) && summary.cards.length > 0
    ? summary.cards
    : [
        { label: 'Total trips', value: summary.totalTrips ?? rows.length },
        { label: 'Total expected km', value: summary.totalExpectedKm ?? '—' },
        { label: 'Total actual km', value: summary.totalActualKm ?? '—' },
        { label: 'Total difference km', value: summary.totalDifferenceKm ?? '—' },
      ];

  const totals = `
    <div class="totals">
      ${cards.map((c) => `<div><strong>${escapeHtml(c.label)}:</strong> ${escapeHtml(c.value ?? '—')}</div>`).join('')}
    </div>
  `;

  const vehicleBlock = vehicleSummary?.rows?.length
    ? `<h2>By vehicle</h2>${renderTable(vehicleSummary.columns || [], vehicleSummary.rows)}<h2>Fill detail</h2>`
    : '';

  const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    body { font-family: Arial, sans-serif; color: #111; padding: 24px; }
    h1 { font-size: 20px; margin: 0 0 6px; }
    h2 { font-size: 14px; margin: 18px 0 8px; }
    .sub { color: #555; margin-bottom: 18px; font-size: 13px; }
    table { width: 100%; border-collapse: collapse; font-size: 11px; margin-bottom: 12px; }
    th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; vertical-align: top; }
    th { background: #f3f4f6; }
    .totals { margin-top: 18px; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 24px; font-size: 13px; }
    @media print {
      body { padding: 0; }
    }
  </style>
</head>
<body>
  <h1>${escapeHtml(title)}</h1>
  <div class="sub">${escapeHtml(subtitleParts.join(' · '))}</div>
  ${vehicleBlock}
  ${renderTable(columns, rows)}
  ${totals}
  <script>window.onload = function () { window.focus(); window.print(); };</script>
</body>
</html>`;

  const win = window.open('', '_blank');
  if (!win) {
    throw new Error('Popup blocked — allow popups to export PDF');
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
}
